import React, { useState, useEffect } from 'react';
import axios from '../../../api/axios';

const ContentManagement = () => {
  const [activeTab, setActiveTab] = useState('banners');
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featuredSections, setFeaturedSections] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'banners') {
        const res = await axios.get('/admin/cms/banners');
        setBanners(res.data);
      } else if (activeTab === 'categories') {
        const res = await axios.get('/admin/cms/categories');
        setCategories(res.data);
      } else if (activeTab === 'featured') {
        const res = await axios.get('/admin/cms/featured-sections');
        setFeaturedSections(res.data);
      }
      
      // Always fetch products for selection
      const prodRes = await axios.get('/admin/products');
      setProducts(prodRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'banners', name: 'Banners & Hero', icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'categories', name: 'Categories', icon: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z' },
    { id: 'featured', name: 'Featured Products', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Content Management</h1>
        <p className="mt-2 text-gray-600">Control what appears on your website</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                ${activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              <svg className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-emerald-500' : 'text-gray-400 group-hover:text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          <p className="mt-2 text-gray-600">Loading...</p>
        </div>
      ) : (
        <>
          {activeTab === 'banners' && <BannersTab banners={banners} onRefresh={fetchData} />}
          {activeTab === 'categories' && <CategoriesTab categories={categories} products={products} onRefresh={fetchData} />}
          {activeTab === 'featured' && <FeaturedTab sections={featuredSections} products={products} onRefresh={fetchData} />}
        </>
      )}
    </div>
  );
};

// Banners Tab Component
const BannersTab = ({ banners, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '', subtitle: '', imageUrl: '', linkUrl: '', buttonText: '', position: 'hero', displayOrder: 0, isActive: true
  });

  const handleEdit = (banner) => {
    setEditing(banner);
    setFormData(banner);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`/admin/cms/banners/${editing.id}`, formData);
      } else {
        await axios.post('/admin/cms/banners', formData);
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ title: '', subtitle: '', imageUrl: '', linkUrl: '', buttonText: '', position: 'hero', displayOrder: 0, isActive: true });
      onRefresh();
      alert(editing ? 'Banner updated!' : 'Banner created!');
    } catch (error) {
      alert('Error saving banner');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this banner?')) {
      try {
        await axios.delete(`/admin/cms/banners/${id}`);
        onRefresh();
      } catch (error) {
        alert('Error deleting banner');
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Manage Banners</h2>
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          style={{backgroundColor: '#059669', color: 'white'}}
          className="px-4 py-2 rounded-lg hover:opacity-90"
        >
          Add Banner
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {banners.map((banner) => (
          <div key={banner.id} className="bg-white border rounded-lg p-4">
            {banner.imageUrl && (
              <img src={banner.imageUrl} alt={banner.title} className="w-full h-32 object-cover rounded mb-3" />
            )}
            <h3 className="font-semibold text-lg">{banner.title}</h3>
            <p className="text-sm text-gray-600 mb-2">{banner.subtitle}</p>
            <div className="flex items-center justify-between">
              <span className={`text-xs px-2 py-1 rounded ${banner.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {banner.isActive ? 'Active' : 'Inactive'}
              </span>
              <div className="space-x-2">
                <button onClick={() => handleEdit(banner)} className="text-emerald-600 hover:text-emerald-900 text-sm">Edit</button>
                <button onClick={() => handleDelete(banner.id)} className="text-red-600 hover:text-red-900 text-sm">Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Banner Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4">{editing ? 'Edit' : 'Add'} Banner</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subtitle</label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({...formData, subtitle: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image URL</label>
                <input
                  type="text"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="/hero_image.jpeg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Button Text</label>
                <input
                  type="text"
                  value={formData.buttonText}
                  onChange={(e) => setFormData({...formData, buttonText: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Link URL</label>
                <input
                  type="text"
                  value={formData.linkUrl}
                  onChange={(e) => setFormData({...formData, linkUrl: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="/products"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Position</label>
                  <select
                    value={formData.position}
                    onChange={(e) => setFormData({...formData, position: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="hero">Hero</option>
                    <option value="secondary">Secondary</option>
                    <option value="promotional">Promotional</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Order</label>
                  <input
                    type="number"
                    value={formData.displayOrder}
                    onChange={(e) => setFormData({...formData, displayOrder: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm">Active</label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" style={{backgroundColor: "#059669", color: "white"}} className="px-4 py-2 rounded-lg hover:opacity-90">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Categories Tab Component
const CategoriesTab = ({ categories, products, onRefresh }) => {
  const [showModal, setShowModal] = useState(false);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', description: '', imageUrl: '', displayOrder: 0, isActive: true
  });

  const handleEdit = (category) => {
    setEditing(category);
    setFormData(category);
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`/admin/cms/categories/${editing.id}`, formData);
      } else {
        await axios.post('/admin/cms/categories', formData);
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ name: '', slug: '', description: '', imageUrl: '', displayOrder: 0, isActive: true });
      onRefresh();
      alert(editing ? 'Category updated!' : 'Category created!');
    } catch (error) {
      alert('Error saving category');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this category?')) {
      try {
        await axios.delete(`/admin/cms/categories/${id}`);
        onRefresh();
      } catch (error) {
        alert('Error deleting category');
      }
    }
  };

  const manageProducts = async (category) => {
    try {
      setSelectedCategory(category);
      // Fetch products in this category
      const catProds = await axios.get(`/admin/cms/categories/${category.id}/products`);
      setCategoryProducts(catProds.data);
      
      // Filter available products (not in this category)
      const catProductIds = catProds.data.map(p => p.id);
      setAvailableProducts(products.filter(p => !catProductIds.includes(p.id)));
      
      setShowProductsModal(true);
    } catch (error) {
      alert('Error loading products');
    }
  };

  const addProductToCategory = async (productId) => {
    try {
      await axios.post(`/admin/cms/categories/${selectedCategory.id}/products/${productId}`);
      manageProducts(selectedCategory); // Refresh
    } catch (error) {
      alert('Error adding product');
    }
  };

  const removeProductFromCategory = async (productId) => {
    try {
      await axios.delete(`/admin/cms/categories/${selectedCategory.id}/products/${productId}`);
      manageProducts(selectedCategory); // Refresh
    } catch (error) {
      alert('Error removing product');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Manage Categories</h2>
        <button
          onClick={() => { setEditing(null); setShowModal(true); }}
          style={{backgroundColor: '#059669', color: 'white'}}
          className="px-4 py-2 rounded-lg hover:opacity-90"
        >
          Add Category
        </button>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {categories.map((cat) => (
              <tr key={cat.id}>
                <td className="px-6 py-4">{cat.name}</td>
                <td className="px-6 py-4 text-sm text-gray-600">{cat.slug}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs rounded ${cat.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}>
                    {cat.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button onClick={() => manageProducts(cat)} className="text-blue-600 hover:text-blue-900">Manage Products</button>
                  <button onClick={() => handleEdit(cat)} className="text-emerald-600 hover:text-emerald-900">Edit</button>
                  <button onClick={() => handleDelete(cat.id)} className="text-red-600 hover:text-red-900">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Category Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
            <h3 className="text-xl font-bold mb-4">{editing ? 'Edit' : 'Add'} Category</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slug *</label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows="3"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image URL</label>
                <input
                  type="text"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Display Order</label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => setFormData({...formData, displayOrder: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
                  className="mr-2"
                />
                <label className="text-sm">Active</label>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-lg">Cancel</button>
                <button type="submit" style={{backgroundColor: "#059669", color: "white"}} className="px-4 py-2 rounded-lg hover:opacity-90">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Products Modal */}
      {showProductsModal && selectedCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">Manage Products</h3>
                  <p className="text-gray-600 mt-1">Category: {selectedCategory.name}</p>
                </div>
                <button
                  onClick={() => setShowProductsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Products in Category */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Products in this Category ({categoryProducts.length})</h4>
                {categoryProducts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {categoryProducts.map((product) => (
                      <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                        <div className="flex items-center gap-3">
                          {product.productImage1 && (
                            <img src={product.productImage1} alt={product.itemName} className="w-12 h-12 object-contain rounded" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{product.itemName}</p>
                            <p className="text-xs text-gray-500">{product.itemCode}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeProductFromCategory(product.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4">No products in this category yet</p>
                )}
              </div>

              {/* Available Products */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Add Products ({availableProducts.length} available)</h4>
                {availableProducts.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto border rounded">
                    {availableProducts.map((product) => (
                      <div key={product.id} className="flex items-center justify-between p-3 hover:bg-gray-50 border-b last:border-b-0">
                        <div className="flex items-center gap-3">
                          {product.productImage1 && (
                            <img src={product.productImage1} alt={product.itemName} className="w-10 h-10 object-contain rounded" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{product.itemName}</p>
                            <p className="text-xs text-gray-500">{product.itemCode} - ₹{product.price1}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => addProductToCategory(product.id)}
                          style={{backgroundColor: '#059669', color: 'white'}}
                          className="px-3 py-1 rounded text-sm hover:opacity-90"
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 py-4">All products are already in this category</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Featured Tab Component
const FeaturedTab = ({ sections, products, onRefresh }) => {
  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Featured Products Sections</h2>
      <p className="text-gray-600 mb-4">Coming soon: Manage which products appear in featured sections on homepage</p>
      <div className="bg-white rounded-lg border p-6">
        <p className="text-sm text-gray-500">This feature will allow you to select products to feature on the homepage</p>
      </div>
    </div>
  );
};

export default ContentManagement;
